import { GROUND, LAND, LAND_BIG, LAND_HUGE, type StructureType } from '@somnhold/content';
import { cellId, geo, gridW, landGround, landStep } from './grid.ts';
import { DIRS, hexDist, hexLen, hexLine, hexRing, hexToWorld, type Hex } from './hex.ts';
import { clamp, fdiv, iatan2, idiv, imin } from './math/fixed.ts';
import type { LandDef, MapDef, SectorDef } from './types.ts';

/**
 * The bastion from scratch (docs/CONSTRUCCION.md): the keep alone on a height, terraced ground that falls
 * away toward the rim, cliffs where a terrace drops sheer, rivers springing below the crest down to the rim
 * with a ford every few cells, lakes in the low hollows, woods, rock and the ruins of the old garrison.
 * Nightmares climb from zones on the rim. Integer-only and a pure function of the seed.
 */

/** A well-mixed 10-bit value for a lattice point. */
const hash = (seed: number, a: number, b: number, salt: number): number => {
  let h = (seed ^ Math.imul(a, 0x27d4eb2d) ^ Math.imul(b, 0x165667b1) ^ Math.imul(salt, 0x9e3779b1)) >>> 0;
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d) >>> 0;
  h = Math.imul(h ^ (h >>> 12), 0x297a2d39) >>> 0;
  return ((h ^ (h >>> 15)) >>> 0) & 1023;
};

/** Value noise in 0–1023 over an axial lattice `step` hexes apart, bilinear between its corners. */
const noise = (seed: number, q: number, r: number, step: number, salt: number): number => {
  const i = fdiv(q, step);
  const j = fdiv(r, step);
  const fq = q - i * step;
  const fr = r - j * step;
  const a = hash(seed, i, j, salt) * (step - fq) + hash(seed, i + 1, j, salt) * fq;
  const b = hash(seed, i, j + 1, salt) * (step - fq) + hash(seed, i + 1, j + 1, salt) * fq;
  return idiv(a * (step - fr) + b * fr, step * step);
};

/** Angle of a hex round the keep, 0–1023. */
const angleOf = (h: Hex): number => {
  const w = hexToWorld(h.q, h.r);
  return (iatan2(w.y, w.x) + 1024) % 1024;
};
const angleGap = (a: number, b: number): number => {
  const d = Math.abs(a - b) % 1024;
  return d > 512 ? 1024 - d : d;
};

/** Cells reachable on foot from `from` (by id), as a 0/1 mask. */
export const landReach = (R: number, land: LandDef, from: number): Uint8Array => {
  const nb = geo(R).nb;
  const seen = new Uint8Array(gridW(R) * gridW(R));
  if (from < 0 || !landGround(land.kind[from] as number)) return seen;
  const queue = [from];
  seen[from] = 1;
  for (let i = 0; i < queue.length; i++) {
    const id = queue[i] as number;
    for (let k = id * 6, end = k + 6; k < end; k++) {
      const n = nb[k] as number;
      if (n < 0 || seen[n] || !landGround(land.kind[n] as number) || landStep(land, id, n) < 0) continue;
      seen[n] = 1;
      queue.push(n);
    }
  }
  return seen;
};

export const createLandMap = (seed: number): MapDef => {
  const R = LAND.radius;
  const W = gridW(R);
  const level = new Array<number>(W * W).fill(0);
  const cliff = new Array<number>(W * W).fill(0);
  const kind = new Array<number>(W * W).fill(GROUND.rock);
  const cells: number[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (hexLen(q, r) <= R) cells.push(cellId(R, q, r));
  const qOf = (id: number): number => (id % W) - R;
  const rOf = (id: number): number => idiv(id, W) - R;

  // 1) Relief: high round the keep, falling toward the rim, broken up by noise.
  for (const id of cells) {
    const q = qOf(id);
    const r = rOf(id);
    const d = hexLen(q, r);
    if (d <= LAND.crest) {
      level[id] = LAND.top;
    } else {
      const slope = idiv((R - d) * 1024, R);
      const relief = noise(seed, q, r, LAND.reliefStep, 1);
      const v = idiv(slope * LAND.slopeBp + relief * (10000 - LAND.slopeBp), 10000);
      level[id] = clamp(idiv((v - 150) * (LAND.top + 1), 760), 0, LAND.top);
    }
    cliff[id] = d > 1 && noise(seed, q, r, 4, 2) > LAND.cliffAt ? 1 : 0;
    kind[id] = GROUND.open;
  }

  // 2) Rivers: each springs just below the crest and runs down and out to the rim, a ford every few cells.
  let water = 0;
  const waterMax = idiv(cells.length * LAND.waterMaxBp, 10000);
  const spring = hexRing(LAND.crest + 2);
  for (let i = 0; i < LAND.rivers; i++) {
    const want = (hash(seed, i, 3, 71) + idiv(i * 1024, LAND.rivers)) % 1024;
    let cur = spring[0] as Hex;
    let best = 0x7fffffff;
    for (const h of spring) {
      const g = angleGap(angleOf(h), want);
      if (g < best) {
        best = g;
        cur = h;
      }
    }
    let run = hash(seed, i, 4, 72) % LAND.fordEvery;
    let lv = level[cellId(R, cur.q, cur.r)] as number;
    for (let guard = 0; guard < R * 3; guard++) {
      const id = cellId(R, cur.q, cur.r);
      if (id < 0) break;
      // Water lies level: it never climbs, and where the ground drops it falls with it.
      lv = imin(lv, level[id] as number);
      level[id] = lv;
      if (kind[id] !== GROUND.water && kind[id] !== GROUND.ford) water++;
      kind[id] = run % LAND.fordEvery === 0 ? GROUND.ford : GROUND.water;
      run++;
      const d = hexLen(cur.q, cur.r);
      if (d >= R) break;
      // Outward, downhill first, then the way the noise leans.
      let next: Hex | null = null;
      let score = 0x7fffffff;
      for (let k = 0; k < 6; k++) {
        const h = { q: cur.q + (DIRS[k] as Hex).q, r: cur.r + (DIRS[k] as Hex).r };
        const nid = cellId(R, h.q, h.r);
        if (nid < 0 || hexLen(h.q, h.r) <= d) continue;
        const s = (level[nid] as number) * 2048 + angleGap(angleOf(h), want) * 2 + (hash(seed, h.q, h.r, 73) & 255);
        if (s < score) {
          score = s;
          next = h;
        }
      }
      if (!next) break;
      cur = next;
    }
  }

  // 3) Lakes in the low hollows, the stillest water first, as far as the share of water allows.
  const lakes: { id: number; n: number }[] = [];
  for (const id of cells) {
    if (level[id] !== 0 || kind[id] !== GROUND.open) continue;
    const n = noise(seed, qOf(id), rOf(id), LAND.coverStep, 81);
    if (n > LAND.lakeAt) lakes.push({ id, n });
  }
  lakes.sort((a, b) => b.n - a.n || a.id - b.id);
  for (const l of lakes) {
    if (water >= waterMax) break;
    kind[l.id] = GROUND.water;
    water++;
  }

  // 4) Rock, woods and the ruins of the old garrison.
  for (const id of cells) {
    if (kind[id] !== GROUND.open) continue;
    const q = qOf(id);
    const r = rOf(id);
    const d = hexLen(q, r);
    if (d > LAND.clear) {
      if (noise(seed, q, r, 3, 91) > LAND.rockAt) kind[id] = GROUND.rock;
      else if (noise(seed, q, r, LAND.coverStep, 92) > LAND.woodAt) kind[id] = GROUND.wood;
    }
    if (kind[id] === GROUND.open && d >= 2 && d <= LAND.ruinsWithin && hash(seed, q, r, 93) * 10000 < LAND.ruinsBp * 1024) kind[id] = GROUND.ruin;
  }
  // Crystal veins open in the rock, apart from one another, the best-drawn first.
  const veinAt: { id: number; h: number }[] = [];
  for (const id of cells) {
    const d = hexLen(qOf(id), rOf(id));
    if (kind[id] === GROUND.rock && d >= LAND.veinMin && d <= R - 3) veinAt.push({ id, h: hash(seed, qOf(id), rOf(id), 95) });
  }
  veinAt.sort((a, b) => b.h - a.h || a.id - b.id);
  const veins: Hex[] = [];
  for (const v of veinAt) {
    if (veins.length >= LAND.veins) break;
    const h = { q: qOf(v.id), r: rOf(v.id) };
    if (veins.some((o) => hexDist(o, h) < LAND.veinApart)) continue;
    veins.push(h);
    kind[v.id] = GROUND.vein;
  }
  // The keep and its yard stay clear.
  for (const id of cells) if (hexLen(qOf(id), rOf(id)) <= 1) kind[id] = GROUND.open;

  // 5) Zones on the rim, spread round it, on ground nightmares can stand on.
  const land: LandDef = { level, cliff, kind, zones: [] };
  const rim = hexRing(R - 1);
  const off = hash(seed, 5, 5, 101);
  for (let i = 0; i < LAND.zones; i++) {
    const want = (off + idiv(i * 1024, LAND.zones)) % 1024;
    let pick = rim[0] as Hex;
    let best = 0x7fffffff;
    for (const h of rim) {
      const id = cellId(R, h.q, h.r);
      const k = kind[id] as number;
      const s = angleGap(angleOf(h), want) * 4 + (k === GROUND.water || k === GROUND.rock || k === GROUND.vein ? 4096 : 0) + (hash(seed, h.q, h.r, 102) & 3);
      if (s < best) {
        best = s;
        pick = h;
      }
    }
    land.zones.push(pick);
  }

  // 6) Every zone has a way up to the keep: where none is left, cut one along the straight line in.
  const keep = cellId(R, 0, 0);
  for (const z of land.zones) {
    const zid = cellId(R, z.q, z.r);
    if (kind[zid] === GROUND.rock || kind[zid] === GROUND.vein || kind[zid] === GROUND.water) kind[zid] = kind[zid] === GROUND.water ? GROUND.ford : GROUND.open;
    if (landReach(R, land, zid)[keep]) continue;
    const line = hexLine(z, { q: 0, r: 0 });
    for (let j = 1; j < line.length; j++) {
      const a = cellId(R, (line[j - 1] as Hex).q, (line[j - 1] as Hex).r);
      const b = cellId(R, (line[j] as Hex).q, (line[j] as Hex).r);
      if (kind[b] === GROUND.rock || kind[b] === GROUND.vein) kind[b] = GROUND.open;
      else if (kind[b] === GROUND.water) kind[b] = GROUND.ford;
      const la = level[a] as number;
      const lb = level[b] as number;
      if (lb > la + 1) level[b] = la + 1;
      else if (lb < la - 1) level[b] = la - 1;
      // The step between them becomes a ramp.
      if (level[b] !== la) cliff[(level[b] as number) > la ? b : a] = 0;
    }
  }

  const sectors: SectorDef[] = land.zones.map((z, index) => {
    const line = hexLine({ q: 0, r: 0 }, z);
    return { index, gate: line[LAND.crest + 2] ?? z, path: [z] };
  });
  return { radius: R, bastion: R, sectors, kind: 'bastion', land };
};

/**
 * The fissures a wave opens (docs/CONSTRUCCION.md): one per sector, out in the field between `LAND.fissureMin`
 * and `LAND.fissureMax` hexes from the keep, on open ground that is outside every wall (reached from the rim
 * without crossing anything that blocks) and with a way up to the keep, at least `LAND.fissureClear` from
 * anything in `blocked` or `built`, and `LAND.fissureApart` from one another. Spread round the keep from an
 * angle drawn from `seed`; a sector with nowhere to open climbs from its zone on the rim. Returns the wave's
 * map, marked `open`.
 */
export const openFissures = (map: MapDef, built: Hex[], blocked: Hex[], seed: number): MapDef => {
  const land = map.land;
  if (!land) return map;
  const R = map.radius;
  const W = gridW(R);
  const nb = geo(R).nb;
  const wall = new Uint8Array(W * W);
  for (const h of blocked) {
    const id = cellId(R, h.q, h.r);
    if (id >= 0) wall[id] = 1;
  }
  // Outside the walls: what the rim reaches on foot without going through anything that blocks.
  const outside = new Uint8Array(W * W);
  const queue: number[] = [];
  for (const h of hexRing(R)) {
    const id = cellId(R, h.q, h.r);
    if (id >= 0 && !wall[id] && landGround(land.kind[id] as number)) {
      outside[id] = 1;
      queue.push(id);
    }
  }
  for (let i = 0; i < queue.length; i++) {
    const id = queue[i] as number;
    for (let k = id * 6, end = k + 6; k < end; k++) {
      const n = nb[k] as number;
      if (n < 0 || outside[n] || wall[n] || !landGround(land.kind[n] as number) || landStep(land, id, n) < 0) continue;
      outside[n] = 1;
      queue.push(n);
    }
  }
  const toKeep = landReach(R, land, cellId(R, 0, 0));
  const near = [...built, ...blocked];
  const spots: Hex[] = [];
  for (let r = -R; r <= R; r++) {
    for (let q = -R; q <= R; q++) {
      const d = hexLen(q, r);
      if (d < LAND.fissureMin || d > LAND.fissureMax) continue;
      const id = cellId(R, q, r);
      const k = land.kind[id] as number;
      // A trench dug by the group cracks like any open ground: digging doesn't choose where the field opens.
      if ((k !== GROUND.open && k !== GROUND.ruin && k !== GROUND.ditch) || !outside[id] || !toKeep[id]) continue;
      const h = { q, r };
      if (near.some((b) => hexDist(b, h) < LAND.fissureClear)) continue;
      spots.push(h);
    }
  }
  // Inside the walls: ground the rim can't reach without breaking through. The skirt: ground just outside
  // a wall that has the inside behind it, round the keep in order.
  const inside: number[] = [];
  const skirt: Hex[] = [];
  const keepCell = cellId(R, 0, 0);
  for (let r = -R; r <= R; r++)
    for (let q = -R; q <= R; q++) {
      if (hexLen(q, r) > R) continue;
      const id = cellId(R, q, r);
      if (!outside[id] && !wall[id] && id !== keepCell && landGround(land.kind[id] as number)) inside.push(id);
    }
  const isIn = new Uint8Array(W * W);
  for (const id of inside) isIn[id] = 1;
  isIn[keepCell] = 1;
  for (let r = -R; r <= R; r++)
    for (let q = -R; q <= R; q++) {
      const id = hexLen(q, r) <= R ? cellId(R, q, r) : -1;
      if (id < 0 || !outside[id]) continue;
      let by = false;
      for (let k = id * 6, end = k + 6; k < end && !by; k++) {
        const n = nb[k] as number;
        if (n < 0 || !wall[n]) continue;
        for (let j = n * 6, e2 = j + 6; j < e2; j++) if ((nb[j] as number) >= 0 && isIn[nb[j] as number]) by = true;
      }
      if (by) skirt.push({ q, r });
    }
  skirt.sort((a, b) => angleOf(a) - angleOf(b) || a.q - b.q || a.r - b.r);
  const enclosed = inside.length > 0;

  const off = hash(seed, 9, 9, 111);
  const opened: Hex[] = [];
  const sectors: SectorDef[] = map.sectors.map((sec, i) => {
    const want = (off + idiv(i * 1024, map.sectors.length) + (hash(seed, i, 1, 112) & 127) - 64 + 1024) % 1024;
    let pick: Hex | null = null;
    let best = 0x7fffffff;
    for (const h of spots) {
      if (opened.some((o) => hexDist(o, h) < LAND.fissureApart)) continue;
      const score = angleGap(angleOf(h), want) * 8 + (hash(seed ^ i, h.q, h.r, 113) & 255);
      if (score < best) {
        best = score;
        pick = h;
      }
    }
    const at = pick ?? (land.zones[i] as Hex | undefined) ?? (sec.path[0] as Hex);
    opened.push(at);
    const way = enclosed ? wayToWall(R, land, outside, wall, isIn, at) : null;
    if (way) return { index: sec.index, gate: way.door, path: way.path };
    const line = hexLine({ q: 0, r: 0 }, at);
    return { index: sec.index, gate: line[imin(LAND.crest + 2, line.length - 1)] ?? at, path: [at] };
  });
  // Where the general may crack more open mid-wave (battle/rifts.ts): the same ground, clear of what was opened.
  const spotIds = spots.filter((h) => !opened.some((o) => hexDist(o, h) < LAND.fissureApart)).map((h) => cellId(R, h.q, h.r));
  spotIds.sort((a, b) => a - b);
  return { ...map, sectors, land: { ...land, open: true, inside, skirt, spots: spotIds } };
};

/**
 * From a fissure to the walls: the shortest way on foot outside them to the wall it meets first that has the
 * inside behind it (the nearer the keep the better, on a tie). `door` is that wall's cell, `path` the way there
 * from the fissure (its last cell beside the door). Null when no wall can be reached.
 */
const wayToWall = (R: number, land: LandDef, outside: Uint8Array, wall: Uint8Array, isIn: Uint8Array, from: Hex): { door: Hex; path: Hex[] } | null => {
  const nb = geo(R).nb;
  const W = gridW(R);
  const start = cellId(R, from.q, from.r);
  if (start < 0) return null;
  const prev = new Int32Array(W * W).fill(-2);
  prev[start] = -1;
  const queue = [start];
  let door = -1;
  let at = -1;
  for (let i = 0; i < queue.length && door < 0; i++) {
    const id = queue[i] as number;
    let pick = -1;
    let near = 0x7fffffff;
    for (let k = id * 6, end = k + 6; k < end; k++) {
      const n = nb[k] as number;
      if (n < 0 || !wall[n]) continue;
      let behind = false;
      for (let j = n * 6, e2 = j + 6; j < e2; j++) if ((nb[j] as number) >= 0 && isIn[nb[j] as number]) behind = true;
      const h = hexLen((n % W) - R, idiv(n, W) - R);
      if (behind && h < near) {
        near = h;
        pick = n;
      }
    }
    if (pick >= 0) {
      door = pick;
      at = id;
      break;
    }
    for (let k = id * 6, end = k + 6; k < end; k++) {
      const n = nb[k] as number;
      if (n < 0 || prev[n] !== -2 || !outside[n] || landStep(land, id, n) < 0) continue;
      prev[n] = id;
      queue.push(n);
    }
  }
  if (door < 0) return null;
  const path: Hex[] = [];
  for (let c = at; c >= 0; c = prev[c] as number) path.push({ q: (c % W) - R, r: idiv(c, W) - R });
  path.reverse();
  return { door: { q: (door % W) - R, r: idiv(door, W) - R }, path };
};

/** Whether a structure takes three cells on terraced land. */
export const isBig = (type: StructureType): boolean => LAND_BIG.includes(type) || LAND_HUGE.includes(type);

/**
 * The cells a structure takes on terraced land: its own, and for a big one the two neighbours `turn` (0–5) and
 * the one after it round the cell, so the three make a triangle.
 */
export const footprint = (type: StructureType, q: number, r: number, turn = 0): Hex[] => {
  // A seven-cell piece: the cell and the ring round it.
  if (LAND_HUGE.includes(type)) return [{ q, r }, ...DIRS.map((d) => ({ q: q + d.q, r: r + d.r }))];
  if (!isBig(type)) return [{ q, r }];
  const t = ((turn % 6) + 6) % 6;
  const a = DIRS[t] as Hex;
  const b = DIRS[(t + 1) % 6] as Hex;
  return [{ q, r }, { q: q + a.q, r: r + a.r }, { q: q + b.q, r: r + b.r }];
};
